import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@new/lib';

export interface SelectOption<T extends string = string> {
  label: string;
  value: T;
}

export interface SelectProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  // Passed by createFormField; accepted and ignored here.
  onBlur?: () => void;
  type?: string;
}

/**
 * Generic form Select. The trigger is styled to match `Input` exactly (same
 * height, background, border, font, radius); the option list opens through a
 * headlessui `Menu` popover (same pattern as `Dropdown`) for a consistent look
 * across platforms. Registered as a form field (`field.Select`) for reuse.
 */
export const Select = <T extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  hasError,
  disabled,
  className,
  // onBlur/type are supplied by createFormField and intentionally unused.
}: SelectProps<T>) => {
  const selected = options.find((o) => o.value === value);

  return (
    <Menu as="div" className={cn('relative w-full', className)}>
      <MenuButton
        disabled={disabled}
        data-slot="select"
        className={cn(
          'l3! bg-(--neutral-white-NW800)! regular flex w-full items-center justify-between gap-2 border-solid',
          'h-[33px] md:h-[50px] xl:h-[66px] p-4! rounded-md! border-2! cursor-pointer',
          'disabled:cursor-not-allowed disabled:opacity-50',
          '[&&]:[font-family:var(--font-montserrat)]! [&&]:[font-size:var(--fs-l3)]! [&&]:leading(--lh-b)!',
          'focus:outline-none! focus:border-(--neutral-black-NB1000)/80!',
          hasError ? 'border-(--danger-D700)/80!' : 'border-transparent/80!'
        )}
      >
        <span
          className={cn(
            'truncate text-left',
            selected ? 'text-(--neutral-black-NB1000)!' : 'text-(--neutral-white-NW600)!'
          )}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className="size-5 shrink-0 text-(--neutral-black-NB600)" />
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom start"
        className="z-[9999] mt-1 max-h-64 w-(--button-width) overflow-auto rounded-md border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) py-1 shadow-lg outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
      >
        {options.map((opt) => (
          <MenuItem key={opt.value}>
            <button
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left',
                '[font-family:var(--font-montserrat)] [font-size:var(--fs-l4)] cursor-pointer',
                'data-[focus]:bg-(--primary-O200)/40',
                value === opt.value
                  ? 'font-semibold text-(--primary-O900)'
                  : 'text-(--neutral-black-NB900)'
              )}
            >
              {opt.label}
              {value === opt.value ? (
                <Check className="size-4 shrink-0 text-(--primary-O900)" />
              ) : null}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};
